import { IDate, Speaker, TimelineChannel } from '@avantstay/arriere-clients/dist/arriereBackoffice'

import { TimelineItem, TimelineItemKind } from '../../StayTimeline.items'
import { GuestInboxCall, GuestInboxCase, GuestInboxTimelineEvent } from '../../StayTimeline.types'
import { toDate } from '../../StayTimeline.utils'
import { splitChatTranscript } from '../chatTranscript'

const SPEAKER_KINDS: Record<Speaker, TimelineItemKind> = {
  [Speaker.guest]: 'in',
  [Speaker.teammate]: 'out',
  [Speaker.vendor]: 'out',
  [Speaker.both]: 'out',
  [Speaker.bot]: 'ai',
  [Speaker.automation]: 'auto',
  [Speaker.system]: 'sys',
}

interface BuildTimelineItemsArgs {
  conversation: GuestInboxTimelineEvent[]
  calls: GuestInboxCall[]
  caseList: GuestInboxCase[]
  guestName?: string | null
}

const getEventKind = (
  event: GuestInboxTimelineEvent,
  call: GuestInboxCall | undefined,
): TimelineItemKind => {
  if (event.channel === TimelineChannel.case) return 'caseNote'
  if (event.internal) return 'note'
  if (event.channel === TimelineChannel.call && call) return 'call'

  return SPEAKER_KINDS[event.speaker] ?? 'out'
}

const getCallKey = (code: string, ticketId?: string | null) => `${code}:${ticketId ?? ''}`

const getTimelineItemTime = (date?: IDate | null) => toDate(date)?.getTime() ?? Number.MAX_VALUE

export const buildTimelineItems = ({
  conversation,
  calls,
  caseList,
  guestName,
}: BuildTimelineItemsArgs): TimelineItem[] => {
  const callsByKey = new Map(calls.map(call => [getCallKey(call.code, call.ticketId), call]))

  const events: TimelineItem[] = conversation
    .flatMap(event => splitChatTranscript({ event, guestName }))
    .map((event, index) => {
      const call = event.callCode
        ? callsByKey.get(getCallKey(event.callCode, event.ticketId))
        : undefined

      return {
        key: event.id ? String(event.id) : `event-${index}`,
        kind: getEventKind(event, call),
        at: event.at,
        who: event.who,
        event,
        call,
      }
    })

  const markers: TimelineItem[] = caseList
    .filter(caseItem => !!caseItem.openedAt)
    .map(caseItem => ({
      key: `case-opened-${caseItem.caseId}`,
      kind: 'caseOpened' as const,
      at: caseItem.openedAt,
      caseItem,
    }))

  const order = (item: TimelineItem) => (item.kind === 'caseOpened' ? 0 : 1)

  const timelineItems = [...events, ...markers]
  return timelineItems.toSorted((a, b) => {
    const aTime = getTimelineItemTime(a.at)
    const bTime = getTimelineItemTime(b.at)

    return aTime - bTime || order(a) - order(b)
  })
}
